import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { HttpClientCustom } from '../custom/http-client';

import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { TidySelectNumberValueModel } from 'src/models/tidy-select-item.model';
import { ToDoListModel, RoomModel, ToDoListRoomModel } from 'src/models/to-do.model';
import { Aws } from '../aws/aws';
import { DateTime as LuxonDateTime } from 'luxon';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { environment } from 'src/environments/environment';

@Injectable()
export class ToDos {

  constructor(
    private http: HttpClient,
    private httpClient: HttpClientCustom,
    private storage: TidyStorage,
    private aws: Aws,
  ) {}

  getStorageIds() {
    return {
      addressId: parseInt(localStorage.getItem('addressId')),
      taskListId: parseInt(localStorage.getItem('taskListId')),
      roomId: parseInt(localStorage.getItem('roomId')),
    }
  }

  getAllToDoLists(perPage, pageIndex, addressIds = null, onlyOrphaned = false, search = null) {
    let url = `api/v1/customer/task-lists?per_page=${perPage}&page=${pageIndex}`;
    if (addressIds) {
      url += `&filters[address_ids]=${addressIds}`;
    }
    if (onlyOrphaned) {
      url += `&filters[only_orphaned]=true`;
    }
    if (search) {
      url += `&filters[search]=${encodeURIComponent(search)}`;
    }
    return this.httpClient.getFullRequest(url)
    .then(response => ({
        body: response.body,
        totalRecords: response.headers.get('X-Total-Records')
      }));
  }

  async getAllToDoListsForAddress(addressId) {
    const url = `api/v1/customer/task-lists?per_page=1000`;
    const response = await this.httpClient.get(url);
    response.map((list) => {
      list.default_list_settings.map((setting) => {
        if (setting.key.includes('default_list.address')) {
          list['is_default'] = true;
        }
      });
    });
    return response;
  }

  isServiceDefault(list, serviceKey): boolean {
    return list.default_list_settings?.some(setting => setting.key.includes(serviceKey)) ?? false;
  }

  getAppliedJobs(addressTaskListId) {
    const url = `api/v1/customer/task-lists/${addressTaskListId}/applied-jobs`;
    return this.httpClient.get(url);
  }

  async getToDosForPastJob(jobId) {
    const url = `api/v1/customer/cleanings/${jobId}/rooms`;
    return await this.httpClient.get(url);
  }

  parseToDoFilterItems(lists, _addressId): TidySelectNumberValueModel[] {
    const parsedLists = [];
    if (lists.length > 0) {
      lists.map((list) => {
        parsedLists.push({
          viewValue: list.title,
          value: list.id
        });
      });
      parsedLists.push({
        viewValue: '+ Add List',
        value: -1
      });
    }
    return parsedLists;
  }

  getSelectedTaskListId(toDoFilter): number {
    let taskListId;
    const storageTaskListId = localStorage.getItem('taskListId');
    if (storageTaskListId !== 'null' && storageTaskListId !== null && storageTaskListId !== '-1' && storageTaskListId !== '-2') {
      taskListId = parseFloat(storageTaskListId);
    } else {
      taskListId = toDoFilter[0].value;
    }
    localStorage.setItem('taskListId', taskListId);
    return taskListId;
  }

  createList(payload) {
    const url = 'api/v1/customer/task-lists';
    return this.httpClient.post(url, payload);
  }

  deleteList(addressTaskListId) {
    const url = `api/v1/customer/task-lists/${addressTaskListId}`;
    return this.httpClient.delete(url);
  }

  updateListTitle(addressTaskListId, title: string, description: string = null) {
    const data = {
      title,
      description
    }
    return this.updateTaskList(addressTaskListId, data);
  }

  updateListBeforeAfterPhotos(addressTaskListId, beforeAfterPhotoState: string) {
    const data = {
      before_after_photos_state: beforeAfterPhotoState
    }
    return this.updateTaskList(addressTaskListId, data);
  }

  updateTaskList(addressTaskListId, payload) {
    const url = `api/v1/customer/task-lists/${addressTaskListId}`;
    return this.httpClient.put(url, payload);
  }

  deleteGroup(addressTaskGroupId, addressTaskListId) {
    const url = `api/v1/customer/address-task-groups/${addressTaskGroupId}/remove-from-task-list`;
    const data = {
      address_task_list_id: addressTaskListId
    }
    return this.httpClient.post(url, data);
  }

  deleteToDo(taskId) {
    const id = this.getStorageIds();
    const url = `api/v1/customer/addresses/${id.addressId}/task-lists/${id.taskListId}/tasks/${taskId}`;
    return this.httpClient.delete(url);
  }

  addGroup(data) {
    const url = 'api/v1/customer/address-task-groups';
    return this.httpClient.post(url, data);
  }

  updateGroup(addressTaskGroupId, payload) {
    const url = `api/v1/customer/address-task-groups/${addressTaskGroupId}/add-to-task-list`;
    return this.httpClient.post(url, payload);
  }

  updateMultipleGroups(taskListId, payload) {
    const url = `api/v1/customer/task-lists/${taskListId}/set-task-groups`;
    return this.httpClient.put(url, payload);
  }

  updateGroupName(addressTaskGroupId, payload) {
    const url = `api/v1/customer/address-task-groups/${addressTaskGroupId}`;
    return this.httpClient.put(url, payload);
  }

  updateListToBeCustomerList(addressId, taskListId, customerListPayload) {
    const url = `api/v1/customer/addresses/${addressId}/task-lists/${taskListId}`;
    return this.httpClient.put(url, customerListPayload)
  }

  addRoom({name, floor, category}) {
    const data = {
      address_room : {
        name,
        floor,
        category
      }
    }
    const id = this.getStorageIds();
    const url = `api/v1/customer/addresses/${id.addressId}/task-lists/${id.taskListId}/rooms`;
    return this.httpClient.post(url, data);
  }

  updateRoom(data) {
    const id = this.getStorageIds();
    const url = `api/v1/customer/addresses/${id.addressId}/rooms/${id.roomId}`;
    return this.httpClient.put(url, data);
  }

  assignListToJob(jobIds, addressTaskListId) {
    const data = {
      job_ids: jobIds,
      address_task_list_id: addressTaskListId
    }
    const url = `api/v1/customer/cleanings/assign-address-task-list-jobs`;
    return this.httpClient.put(url, data);
  }

  updateDefaultList(addressId, addressTaskListId) {
    const data = {
      address_task_list_id: addressTaskListId
    }
    const url = `api/v1/customer/addresses/${addressId}/assign-default-address-task-list`;
    return this.httpClient.put(url, data);
  }

  saveTaskListSettings(taskListId, payload: {settings: {key: string, value: any}[]}) {
    const url = `api/v1/customer/task-lists/${taskListId}/settings`;
    return this.httpClient.put(url, payload);
  }

  saveDefaultList(taskListId, payload) {
    return this.saveTaskListSettings(taskListId, payload);
  }

  saveBlockCompleteOnMissingFields(taskListId, value: boolean) {
    const payload = {
      settings: [{
        key: `block_complete_on_missing_fields`,
        value
      }]
    };
    return this.saveTaskListSettings(taskListId, payload);
  }

  updateRoomPriority(addressTaskListId, orderedRoomIds) {
    const data = {
      address_task_list_id: addressTaskListId,
      address_task_group_ids_in_order: orderedRoomIds
    };
    const url = `api/v1/customer/task-lists/${addressTaskListId}/set-groups-display-order`;
    return this.httpClient.put(url, data);
  }

  getListDetail(addressTaskListId): Promise<RoomModel[]> {
    const url = `api/v1/customer/task-lists/${addressTaskListId}/tasks?includes=tasks,notes,photos`;
    return this.httpClient.get(url);
  }

  addToDo(addressId, taskListId, payload) {
    const url = `api/v1/customer/addresses/${addressId}/task-lists/${taskListId}/tasks`;
    return this.httpClient.post(url, payload);
  }

  updateToDo(taskId, payload) {
    const id = this.getStorageIds();
    const url = `api/v1/customer/addresses/${id.addressId}/task-lists/${id.taskListId}/tasks/${taskId}`;
    return this.httpClient.put(url, payload);
  }

  addRoomNote(groupId: number, body: string) {
    const payload = {
      text_notes: [{
        type: 'address_task_group',
        object_id: groupId,
        body
      }]
    };
    const url = `${environment.api_url}api/v1/customer/text-notes`;
    return firstValueFrom(this.http.post(url, payload));
  }

  updateRoomNote(noteId: number, body: string) {
    const payload = {
      body
    };
    const url = `${environment.api_url}api/v1/customer/text-notes/${noteId}`;
    return firstValueFrom(this.http.put(url, payload));
  }

  updateTextNote(noteId: number, body: string) {
    return this.updateRoomNote(noteId, body);
  }

  deleteRoomNote(noteId: number) {
    const url = `${environment.api_url}api/v1/customer/text-notes/${noteId}`;
    return firstValueFrom(this.http.delete(url));
  }

  deleteTextNote(noteId: number) {
    return this.deleteRoomNote(noteId);
  }

  parseRoomCheckboxItems(rooms): TidySelectNumberValueModel[] {
    const parsedRooms = [];
    if (rooms.length > 0) {
      rooms.map((room) => {
        parsedRooms.push({
          viewValue: room.name,
          value: room.id
        });
      });
    }
    return parsedRooms;
  }

  important(important) {
    if (important) {
      return {
        'background-color': 'rgba(254, 205, 78, 0.2)',
        'padding' : '2px'
      };
    };
  }

  combineListResponse(toDoLists: ToDoListModel): ToDoListRoomModel[] {
    const lists = [];
    if (toDoLists.default_list !== null) {
      toDoLists.default_list['is_default'] = true;
      lists.push(toDoLists.default_list);
    }
    toDoLists.non_default_lists.map((list) => {
      list['is_default'] = false;
      lists.push(list);
    })
    return lists;
  }

  buildRoomIcons(rooms) {
    let bedroomCount = 1;
    let bathroomCount = 1;
    const formattedRooms = rooms.map((room) => {
      if (room.category === 'bedroom') {
        room['icon'] = `bedroom_${bedroomCount}`;
        bedroomCount += 1;
      } else if (room.category === 'bathroom') {
        room['icon'] = `bathroom_${bathroomCount}`;
        bathroomCount += 1;
      } else {
        room['icon'] = room.category;
      }
      return room;
    })
    return formattedRooms;
  }

  async roomPhotoNote(roomId, awsKey: string, photoUrl?: string) {
    const url = `${environment.api_url}api/v1/customer/photo-notes`;
    const payload = {
      photo_notes: [{
        type: 'room',
        object_id: roomId,
        photo_url: photoUrl || awsKey,
        s3_photo_url_key: awsKey
      }]
    };
    return firstValueFrom(this.http.post(url, payload));
  }

  async addGroupPhotoNote(groupId: number, photoUrl: string, caption?: string) {
    const url = `${environment.api_url}api/v1/customer/photo-notes`;
    const payload = {
      photo_notes: [{
        type: 'group',
        object_id: groupId,
        photo_url: photoUrl,
        caption: caption || null
      }]
    };
    const response: any = await firstValueFrom(this.http.post(url, payload));
    return Array.isArray(response) ? response[0] : response;
  }

  async addRoomObjectPhotoNote(roomObjectId: number, photoUrl: string, caption?: string) {
    const url = `${environment.api_url}api/v1/customer/photo-notes`;
    const payload = {
      photo_notes: [{
        type: 'room_object',
        object_id: roomObjectId,
        photo_url: photoUrl,
        caption: caption || null
      }]
    };
    const response: any = await firstValueFrom(this.http.post(url, payload));
    return Array.isArray(response) ? response[0] : response;
  }

  async taskPhotoNotes(roomId, taskId, awsKey: string, photoUrl?: string) {
    const url = `${environment.api_url}api/v1/customer/photo-notes`;
    const payload = {
      photo_notes: [{
        type: 'task',
        object_id: taskId,
        photo_url: photoUrl || awsKey,
        s3_photo_url_key: awsKey
      }]
    };
    return firstValueFrom(this.http.post(url, payload));
  }

  async addGroupTaskPhotoNote(taskGroupId: number, taskId: number, photoUrl: string, caption?: string) {
    const url = `${environment.api_url}api/v1/customer/photo-notes`;
    const payload = {
      photo_notes: [{
        type: 'task',
        object_id: taskId,
        photo_url: photoUrl,
        caption: caption || null
      }]
    };
    const response: any = await firstValueFrom(this.http.post(url, payload));
    return Array.isArray(response) ? response[0] : response;
  }

  async addPhotoNotesBatch(photoNotes: { type: string; object_id: number; photo_url: string; caption?: string }[]) {
    if (!photoNotes.length) {return [];}
    const url = `${environment.api_url}api/v1/customer/photo-notes`;
    // The endpoint dedupes photo_notes by object_id within a single request, so a
    // task/group with multiple photos would lose all but the last. Split into the
    // minimum number of requests where each object_id appears at most once.
    const notesByObject = new Map<number, typeof photoNotes>();
    for (const note of photoNotes) {
      const existing = notesByObject.get(note.object_id);
      if (existing) {
        existing.push(note);
      } else {
        notesByObject.set(note.object_id, [note]);
      }
    }
    const objectNoteLists = [...notesByObject.values()];
    const batchCount = Math.max(...objectNoteLists.map(notes => notes.length));
    const responses: any[] = [];
    for (let i = 0; i < batchCount; i++) {
      const batch = objectNoteLists
        .map(notes => notes[i])
        .filter(Boolean);
      const payload = {
        photo_notes: batch.map(pn => ({
          type: pn.type,
          object_id: pn.object_id,
          photo_url: pn.photo_url,
          caption: pn.caption || null,
        }))
      };
      responses.push(await firstValueFrom(this.http.post(url, payload)));
    }
    return responses;
  }

  getTaskCategoryOptions() {
    const url = 'api/v1/customer/task-category-options';
    return this.httpClient.get(url);
  }

  getVideoInspectionSettings(rooms) {
    let inspectionSetting = null;
    let hasBeforeVideos = false;
    rooms.map((room) => {
      room.tasks.map((task) => {
        if (task.task_type === 'take_before_video') {
          hasBeforeVideos = true;
          inspectionSetting = 'Before Videos';
        } else if (task.task_type === 'take_after_video') {
          inspectionSetting = hasBeforeVideos ? 'Before & After Videos' : 'After Videos';
        }
      });
    });
    return inspectionSetting;
  }

  getRemoteInspectionStatus(state) {
    const states = {
      photo: new TranslationPipe().transform('Before & After Photos'),
      video: new TranslationPipe().transform('Before & After Videos'),
      before_photo_only: new TranslationPipe().transform('Before Photos'),
      after_photo_only: new TranslationPipe().transform('After Photos'),
      before_video_only: new TranslationPipe().transform('Before Videos'),
      after_video_only: new TranslationPipe().transform('After Videos'),
      inactive: new TranslationPipe().transform('No Inspection')
    }
    return states[state] || 'OFF';
  }

  getCustomFieldIcon(fieldName) {
    const icons = {
      'Take Inventory': 'assets/img/count.svg',
      'Select': 'assets/img/radio-button.svg',
      'Text Area': 'assets/img/text-area.svg',
      'Checkbox(es)':  'assets/img/checkbox.svg',
      'Take Photo':  'assets/img/take-photo.svg',
      'Take Photos':  'assets/img/take-photo.svg',
    }
    return icons[fieldName];
  }

  copyListToAddress(fromAddressId, taskListId, payload) {
    const url = `api/v1/customer/addresses/${fromAddressId}/task-lists/${taskListId}/copy-task-list`;
    return this.httpClient.post(url, payload);
  }

  updateToDoOrder(taskListId, payload) {
    const url = `api/v1/customer/task-lists/${taskListId}/set-tasks-display-order`;
    return this.httpClient.put(url, payload);
  }

  setItemsDisplayOrder(groupId: number, itemsInOrder: { type: string; id: number }[]) {
    const url = `${environment.api_url}api/v1/customer/address-task-groups/${groupId}/set-items-display-order`;
    const payload = {
      id: groupId,
      items_in_order: itemsInOrder
    };
    return firstValueFrom(this.http.put(url, payload));
  }

  async getMetadataFromFiles(rooms: any[]): Promise<any[]> {
    try {
      for (const room of rooms) {
        const files = room?.before_after_photos;
        const todos = room?.todos;

        if (files?.length > 0) {
          await this.processFiles(files);
        }

        if (todos?.length > 0) {
          for (const todo of todos) {
            await this.processTodoOptions(todo.options);
          }
        }
      }
      return rooms;
    } catch (_err) {
      return rooms;
    }
  }

  async processFiles(files: any[]): Promise<void> {
    for (const file of files) {
      file.metadata = await this.getMetadata(file.photo_url);
    }
  }

  async processFilesAndReturn(files: any[]): Promise<any[]> {
    for (const file of files) {
      file.metadata = await this.getMetadata(file.media_url);
    }
    return files;
  }

  async processTodoOptions(options: any[]): Promise<void> {
    const availableTypes = ['photo', 'video', 'photos', 'videos'];
    for (const option of options) {
      if (availableTypes.includes(option?.value_type)) {
        if (option?.value) {
          option.metadata = await this.getMetadata(option.value);
        }
        if (option?.customer_value) {
          const jsonValues = JSON.parse(option.customer_value);
          option.jsonValues = await Promise.all(jsonValues.map(async (value) => ({
            value,
            metadata: await this.getMetadata(value)
          })));
        } else if (option?.json_value) {
          if (typeof option.json_value === 'string') {
            option.jsonValues = [{
              value: option.json_value,
              metadata: await this.getMetadata(option.json_value)
            }];
          } else if (Array.isArray(option.json_value)) {
            option.jsonValues = await Promise.all(option.json_value.map(async (jsonValue) => ({
                value: jsonValue,
                metadata: await this.getMetadata(jsonValue)
              })));
          }
        }
      }
    }
  }

  async getMetadata(url: string) {
    const metadata = await this.aws.getObjectMetadata(url);
    if (!metadata) {
      return null;
    }
    const dateInUtc = LuxonDateTime.fromISO(metadata.date, { zone: 'utc' });
    const displayDate = dateInUtc.setZone(metadata.timezone).toISO();
    const isInvalidLocation =
      metadata?.latitude === '0' ||
      metadata?.longitude === '0' ||
      metadata?.latitude === 'NaN' ||
      metadata?.longitude === 'NaN' ||
      !metadata?.latitude ||
      !metadata?.longitude;
    const location = isInvalidLocation ? new TranslationPipe().transform('Location unavailable - using property location for report') : `${metadata.latitude}, ${metadata.longitude}`;
    return {
      ...metadata,
      displayDate,
      location,
    };
  }

  updateInputedToDos(payload, jobId) {
    const url = `api/v1/customer/cleanings/${jobId}/to-dos`;
    return this.httpClient.put(url, payload);
  }

  getToDoIcon(performance) {
    if (performance === 'performed') {
      return 'assets/img/to-do-done.svg';
    } else if (performance === 'not_performed') {
      return 'assets/img/to-do-not-done.svg';
    } else {
      return 'assets/img/to-do-not-marked.svg';
    }
  }

  getToDoTooltip(performance) {
    if (performance === 'performed') {
      return new TranslationPipe().transform('Marked as performed by pro');
    } else if (performance === 'not_performed') {
      return new TranslationPipe().transform('Marked as not performed by pro');
    } else {
      return new TranslationPipe().transform('Not marked by pro');
    }
  }

  async getToDoListItemsForAddress(addressId, lastOptionName) {
    let allToDoListItems: any[] = [];
    const allToDoLists = await this.getAllToDoListsForAddress(addressId);
    allToDoListItems = allToDoLists.map((list) => ({
      value: list.id,
      viewValue: list?.title,
      defaultListSettings: list?.default_list_settings,
      description: list?.description
    }));
    allToDoListItems.push({
      value: 0,
      viewValue: lastOptionName,
      defaultListSettings: []
    });
    return allToDoListItems;
  }

  async getAllToDoListItems(lastOptionName) {
    let allToDoListItems: any[] = [];
    const response = await this.getAllToDoLists(1000, 1, null);
    const allToDoLists = response.body || [];
    allToDoListItems = allToDoLists.map((list) => ({
      value: list.id,
      viewValue: list?.title,
      defaultListSettings: list?.default_list_settings,
      description: list?.description
    }));
    allToDoListItems.push({
      value: 0,
      viewValue: lastOptionName,
      defaultListSettings: []
    });
    return allToDoListItems;
  }

  async getAllIsCustomerToDoLists() {
    const response = await this.getAllToDoLists(1000, 1, null);
    return response.body || [];
  }


}
